<template>
    <vue-draggable-resizable
        v-if="visible"
        :id="cue.id"
        ref="cue-handler"
        class="ql-cue md-card" 
        :class="[{ 'cue-editing': isEditing || isEditingDescription || isEditingTime, 'cue-expanded': expanded, 'ql-cue--noteslist-linked': notesListLinked }, 'ql-cue-' + cue.id]"
        :handles="[]"
        :style="styles"
        :resizable="false" :draggable="isFloatingCue"
        :disableUserSelect="true"
        :x="floatingX"
        :y="floatingY"
        :key="handler"
        @dragging="onDrag"
        @dragstop="onDragStop"
        @mousedown.native="selectForCopy"
        drag-handle=".drag"
    >
        <project-cues-cue-header
            :expanded="expanded"
            :isEditing="isEditing"
            :isEditingDescription="isEditingDescription"
            :isEditingTime="isEditingTime"
            :cue="cue"
            :layer="layer"
            :layers="editableLayers"
            :locked="locked"
            :editable="editable"
            :color="color"
            :selectedScale="selectedScale"
            :isFloatingCue="isFloatingCue"
            :focusInputWorkNote="focusInputWorkNote"
            :svgContentWidth="svgContentWidth"
            :svgTrueHeight="svgTrueHeight"
            @toggle="toggle"
            @cueUpdated="updatedCue"
            @updateStatus="updateStatus"
            @tetherCue="tetherCue"
            @untetherCue="untetherCue"
            @focusWorkNote="focusInputWorkNote"
            ref="ql-cue-header"
        />
        <project-cues-cue-body v-show="expanded"
            :id="'cue-body-box-attr-' + cue.id"
            :cue="cue"
            :locked="locked"
            :editable="editable"
            :color="color"
            :selectedScale="selectedScale"
            :isFloatingCue="isFloatingCue"
            :userDefined="getUserDefined"
            :svgContentWidth="svgContentWidth"
            :svgTrueHeight="svgTrueHeight"
            @toggle="toggle"
            @cueUpdated="updatedCue"
            @destroy="destroy" />
    </vue-draggable-resizable>
</template>

<script>
import { mapGetters } from 'vuex';
import vueDragResize from 'vue-draggable-resizable';
import NavBus from '@/buses/nav';
import { NOTESLIST_INTEGRATION_ENABLED } from '@/lib/URLs';

/**
 * Component For...
 */
import ProjectCuesCueHeader from '@/components/project/cues/cue-header';
import ProjectCuesCueBody from '@/components/project/cues/cue-body';

const ProjectCuesCue = {
    name: 'projectCuesCue',
    props: ['cue', 'open', 'locked', 'cuesListWidth', 'cueIndex', 'svgContentWidth', 'svgTrueHeight', 'currentPage', 'openCues'],
    data: function() {
        return {
            expanded: this.open,
            isEditing: false,
            isEditingDescription: false,
            isEditingTime: false,
            isDragging: false,
            originWidth: 450,
            draggingX: 0,
            draggingY: 0,
            tethering: false,
            handler: 0,
            userDefined: {
                user1: { label: 'User 1', value: false },
                user2: { label: 'User 2', value: false },
                user3: { label: 'User 3', value: false },
                user4: { label: 'User 4', value: false }
            }
        };
    },
    watch: {
        isFloatingCue() {
            this.handler++;
        },
        floatingX() {
            if (!this.isDragging) {
                this.handler++;
            }
        },
        floatingY() {
            if (!this.isDragging) {
                this.handler++;
            }
        }
    },
    computed: {
        ...mapGetters([
            'userProjectData',
            'userProjectByUidData',
            'titleFontSizeOptions',
            'layerByCue',
            'editableLayers',
            'floatingCues',
            'cuePlacement',
            'selectedScale',
            'selectedPageType',
            'selectedThumbnailView',
            'splitView',
            'getDefaultCuePlacement',
            'showMarginView',
            'visibleCues',
            'project'
        ]),
        notesListLinked() {
            // !! so this is a Boolean, not the production id string. It binds
            // straight to Boolean props (:disabled), which warn on a String.
            return !!(NOTESLIST_INTEGRATION_ENABLED
                && this.project
                && this.project.noteslist_link_status === 'linked'
                && this.project.noteslist_production_id);
        },
        layer() {
            return this.layerByCue(this.cue);
        },
        inSplitView() {
            return this.splitView === 'true' && this.selectedThumbnailView === 'false';
        },
        isFloatingCue() {
            return Object.keys(this.floatingCues).includes(this.cue.id);
        },
        floatingX() {
            return this.isFloatingCue ? this.floatingCues[this.cue.id].left * this.selectedScale : 0;
        },
        floatingY() {
            return this.isFloatingCue ? this.floatingCues[this.cue.id].top * this.selectedScale : 0;
        },
        visible() {
            let visible = true;

            if (this.layer) {
                if (this.layer.hidden === true) {
                    visible = false;
                }
            }
            return visible;
        },
        editable() {
            if (this.layer) {
                const filter = this.editableLayers.filter(l => l.id === this.layer.id);
                return filter.length > 0;
            }
            return false;
        },
        color() {
            let val = 'white';
            if (this.layer) {
                val = this.layer.color;
            }
            return val;
        },
        titleFontSize() {
            if (this.titleFontSizeOptions[this.cue.id]) {
                return this.titleFontSizeOptions[this.cue.id];
            }
            if (this.cue.titleFontSize) {
                return this.cue.titleFontSize;
            }

            const layers = this.userProjectData.layers;
            if (layers && this.layer && layers[this.layer.id]) {
                if (layers[this.layer.id].titleFontSize) {
                    return layers[this.layer.id].titleFontSize;
                }
            }
            return this.layer.titleFontSize || 14;
        },
        styles() {
            const cuePlaceHeight = (Number(((this.titleFontSize)) + 2).toFixed(5)) * this.selectedScale;
            const defaultCuePlacement = this.getDefaultCuePlacement(this.layer.id);
            let cueMarginTop = 0;
            if (this.selectedScale >= 0.5 && this.selectedScale <= 1.51) {
                cueMarginTop = 1;
            } else if (this.selectedScale >= 1.52 && this.selectedScale <= 2.51) {
                cueMarginTop = 0.92;
            } else if (this.selectedScale >= 2.52 && this.selectedScale <= 3.01) {
                cueMarginTop = 0.9;
            } else if (this.selectedScale >= 3.02 && this.selectedScale <= 4.01) {
                cueMarginTop = 0.85;
            }
            const styles = {
                borderColor: this.color,
                borderWidth: `${Number(1 / this.selectedScale).toFixed(5)}px`,
                top: `${this.cue.offsetY + ((this.cue.type === 'position' ? (this.cue.height / 2) : this.cue.height) * this.selectedScale)}px`,
                zIndex: (this.expanded ? 100 : 11),
                transform: `scale(${this.selectedScale})`,
                transformOrigin: `left ${Number(((this.cue.titleFontSize || this.layer.titleFontSize || 14) * 1.1) + 2).toFixed(5)}px`,
                marginTop: `${0 - ((cuePlaceHeight - (cuePlaceHeight * (this.selectedScale - cueMarginTop))))}px`,
                height: (this.expanded ? 'auto' : `${cuePlaceHeight / this.selectedScale}px`),
                width: 'auto'
            };
            if (this.isFloatingCue) {
                styles.position = 'absolute';
                styles.left = (this.expanded ? 'auto' : `${this.floatingX}px`);
                styles.top = `${this.floatingY + (12 * this.selectedScale)}px`;
                styles.transformOrigin = `left ${Number(((this.titleFontSize) * 1.1 * this.selectedScale) + 2).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'leftEdge') {
                styles.left = '2px';
                styles.transform = `translateX(calc(-${this.selectedScale * 100}% - 12px)) scale(${this.selectedScale})`;
                styles.transformOrigin = `left ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'rightEdge') {
                styles.left = 'auto';
                styles.right = '2px';
                styles.transform = `translateX(calc(${this.selectedScale * 100}% + 12px)) scale(${this.selectedScale})`;
                styles.transformOrigin = `right ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'right') {
                styles.left = 'auto';
                styles.right = '2px';
                styles.transformOrigin = `right ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            } else if (defaultCuePlacement === 'left') {
                styles.left = '2px';
                styles.transformOrigin = `left ${Number((((this.titleFontSize) * 1.1 * this.selectedScale) + 2) + (this.selectedScale * 1)).toFixed(5)}px`;
            }
            if (this.inSplitView && this.expanded && !this.isFloatingCue) {
                // In the narrow split pane the off-pane margin isn't visible, so an
                // expanded box translated into it gets clipped by the pane edge / hidden
                // under the list. Anchor the box's right edge inside the pane and drop the
                // translate-into-margin so it opens over the page instead; the body then
                // gets a max size + scroll (see .page-viewer.split-view CSS) to stay in-pane.
                styles.left = 'auto';
                styles.right = '2px';
                styles.transform = `scale(${this.selectedScale})`;
                styles.transformOrigin = 'right top';
            }
            return styles;
        },
        getUserDefined() {
            const layer = this.layerByCue(this.cue);
            if (layer && layer.userDefined) {
                return layer.userDefined;
            }
            return {
                user1: { label: 'User 1', value: false },
                user2: { label: 'User 2', value: false },
                user3: { label: 'User 3', value: false },
                user4: { label: 'User 4', value: false }
            };
        }
    },
    methods: {
        // The last cue box you pressed is the one Cmd-C copies (views/project.vue, lib/cue-paste.js).
        selectForCopy() {
            NavBus.$emit('cue-selected', this.cue);
        },
        toggle() {
            if (this.$props.cue) {
                if (this.expanded) {
                    this.contract();
                } else {
                    this.expand();
                }
            }
        },
        expand() {
            this.$nextTick(() => {
                this.expanded = true;
                this.$emit('openCue', this.cue);
            });
        },
        contract() {
            this.expanded = false;
            this.$emit('closeCue', this.cue);
        },
        destroy() {
            this.$emit('destroy', this.$props.cue);
        },
        updatedCue(cue) {
            this.$emit('updatedCue', cue);
        },
        updateStatus({isEditing = false, isEditingDescription = false, isEditingTime = false}) {
            this.isEditing = isEditing;
            this.isEditingDescription = isEditingDescription;
            this.isEditingTime = isEditingTime;
        },
        onDrag(left, top) {
            if (this.tethering) {
                this.isDragging = false;
                return false;
            }
            let possibleLeft = Math.max(0, left);
            let possibleTop = top < 0 ? 7 : top;

            const offCueWidth = document.getElementById(this.cue.id).offsetWidth;
            const offCueHeight = document.getElementById(this.cue.id).offsetHeight;
            possibleLeft = possibleLeft > (this.svgContentWidth - offCueWidth) ? (this.svgContentWidth - (this.selectedScale * offCueWidth)) : possibleLeft;
            possibleTop = possibleTop > (this.svgTrueHeight - offCueHeight) ? (this.svgTrueHeight - (this.selectedScale * (offCueHeight - 6))) : possibleTop;
            this.floatingCues[this.cue.id].left = (possibleLeft / this.selectedScale);
            this.floatingCues[this.cue.id].top = possibleTop / this.selectedScale;

            this.isDragging = true;
            return true;
        },
        onDragStop(left, top) {
            this.isDragging = false;
            let possibleLeft = Math.max(0, left);
            let possibleTop = top < 0 ? 7 : top;

            const offCueWidth = document.getElementById(this.cue.id).offsetWidth;
            const offCueHeight = document.getElementById(this.cue.id).offsetHeight;
            possibleLeft = possibleLeft > (this.svgContentWidth - offCueWidth) ? (this.svgContentWidth - (this.selectedScale * offCueWidth)) : possibleLeft;
            possibleTop = possibleTop > (this.svgTrueHeight - offCueHeight) ? (this.svgTrueHeight - (this.selectedScale * (offCueHeight - 6))) : possibleTop;

            if (!this.tethering) {
                this.$store.dispatch('addFloatingCue', {
                    id: this.cue.id,
                    left: possibleLeft / this.selectedScale,
                    top: possibleTop / this.selectedScale
                });
            }
        },
        tetherCue() {
            this.tethering = true;
        },
        untetherCue() {
            this.tethering = false;
        },
        focusInputWorkNote() {
            NavBus.$emit('cue-opened-with-right-click');
        },
        cueAttributeBoxAllClose(e) {
            if (!this.showMarginView) {
                if (e.target.closest('.ql-cue-layer-func-back') || e.target.closest('.md-card-content.ql-cue-body') || e.target.closest('.md-backdrop.md-menu-backdrop.md-transparent.md-active') || e.target.closest('.md-button.md-button-ghost.md-theme-default') || e.target.closest('.ql-cue-layer-picker-content') || e.target.closest('.ql-cue-menu-content')) {
                    return;
                }
                // Note: structuredClone() not available in Node 11.15.0, using JSON.parse(JSON.stringify()) as fallback
                const openCues = JSON.parse(JSON.stringify(this.openCues));
                const allCues = this.visibleCues(this.currentPage);
                if (allCues.length > 0 && openCues.length > 0) {
                    for (const cueItem of allCues) {
                        // Note: structuredClone() not available in Node 11.15.0, using JSON.parse(JSON.stringify()) as fallback
                        const cue = JSON.parse(JSON.stringify(cueItem));
                        // Null-guarded: a cue can be listed before its box is in the DOM.
                        // (A block here used to set .style on an HTMLCollection, which threw
                        // "Cannot set properties of undefined (setting 'display')" on every
                        // click while a cue menu was open and aborted this loop. Removed 1 Oct
                        // 2026; it never hid anything, and clicks inside that menu now return
                        // early above.)
                        const attrBox = document.getElementById('cue-body-box-attr-' + cue.id);
                        if (attrBox) {
                            attrBox.style.display = 'none';
                        }
                        this.expanded = false;
                    }
                }
            }
            if (this.showMarginView && e.target.className === 'md-layout ql-pages-container') {
                if (this.selectedThumbnailView !== 'true') {
                    const leftEdgeElements = document.querySelectorAll('.leftEdge');

                    let maxWidth = 0;
                    let maxElement = null;

                    // Loop through the elements and find the one with the highest width
                    for (const element of leftEdgeElements) {
                        const rect = element.getBoundingClientRect();
                        const width = rect.width;
                        const elementWidth = width;
                        if (elementWidth > maxWidth) {
                            maxWidth = elementWidth;
                            maxElement = element;
                        }
                    }
                    const rightEdgeElements = document.querySelectorAll('.rightEdge');

                    let maxRightWidth = 0;
                    let maxRightElement = null;

                    // Loop through the elements and find the one with the highest width
                    for (const element of rightEdgeElements) {
                        const rect = element.getBoundingClientRect();
                        const width = rect.width;
                        const elementWidth = width;
                        if (elementWidth > maxRightWidth) {
                            maxRightWidth = elementWidth;
                            maxRightElement = element;
                        }
                    }

                    const fullWidth = Number.parseInt(((Math.max(maxWidth, maxRightWidth)) + this.svgContentWidth + (Math.max(maxWidth, maxRightWidth))), 10);
                    if ((fullWidth > window.innerWidth) && (maxElement || maxRightElement)) {
                        const divElement = document.querySelector('.page-viewer');
                        divElement.style.width = (Number.parseInt(((Math.max(maxWidth, maxRightWidth)) + this.svgContentWidth + (Math.max(maxWidth, maxRightWidth))), 10) + (50)) + 'px';
                        divElement.style.margin = '0 auto';
                    } else if (document.querySelector('.page-viewer')) {
                        document.querySelector('.page-viewer').style.width = 'auto';
                        document.querySelector('.page-viewer').style.margin = '0';
                    }
                }
                // Note: structuredClone() not available in Node 11.15.0, using JSON.parse(JSON.stringify()) as fallback
                const openCues = JSON.parse(JSON.stringify(this.openCues));
                const allCues = this.visibleCues(this.currentPage);
                if (allCues.length > 0 && openCues.length > 0) {
                    for (const cueItem of allCues) {
                        // Note: structuredClone() not available in Node 11.15.0, using JSON.parse(JSON.stringify()) as fallback
                        const cue = JSON.parse(JSON.stringify(cueItem));
                        // Null-guarded: a cue can be listed before its box is in the DOM.
                        // (A block here used to set .style on an HTMLCollection, which threw
                        // "Cannot set properties of undefined (setting 'display')" on every
                        // click while a cue menu was open and aborted this loop. Removed 1 Oct
                        // 2026; it never hid anything, and clicks inside that menu now return
                        // early above.)
                        const attrBox = document.getElementById('cue-body-box-attr-' + cue.id);
                        if (attrBox) {
                            attrBox.style.display = 'none';
                        }
                        this.expanded = false;
                    }
                }
            }
        }
    },
    components: {
        'project-cues-cue-body': ProjectCuesCueBody,
        'project-cues-cue-header': ProjectCuesCueHeader,
        'vue-draggable-resizable': vueDragResize
    },
    mounted: function() {
        document.addEventListener('click', this.cueAttributeBoxAllClose);
        document.addEventListener('touchend', this.cueAttributeBoxAllClose);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.cueAttributeBoxAllClose);
        document.removeEventListener('touchend', this.cueAttributeBoxAllClose);
    }
};

export default ProjectCuesCue;
</script>

<style lang="scss">
.md-backdrop{
    position: fixed;
}
.ql-cue-list {
    .ql-cue {
        position: absolute;
        margin-bottom: 5px;
        top: 0;
        box-sizing: border-box;
        border: 1px solid #000;
        border-radius: 3px;
        overflow: hidden;
        .md-card {
            width: 100%;
        }

        &.cue-editing, &.cue-expanded {
            width: auto !important;
        }

        &.ql-cue--noteslist-linked {
            overflow: visible;
        }
    }
}
.md-theme-default .ql-cue {
    background-color: white;
}
.md-theme-show-mode .ql-cue {
    background-color: #424242;
}
</style>


// WEBPACK FOOTER //
// cue.vue?62e9bcdb